import { fabric } from 'fabric';
import tools from '../tools';
import type FrameMemberBehaviors from './FrameMemberBehaviors';
import { type RecordId } from '../types';
import FrameBehaviors from './FrameBehaviors';
import { type IEvent, type IObjectOptions } from 'fabric/fabric-impl';
import LayoutBehaviors from './LayoutBehaviors';
import { type ConnectorOptions } from './Connector';
import MindMapConnector from './MindMapConnector';
import MindMapBranch from './MindMapBranch';
import { isMindMapBranch, isMindMapConnector, isMindMapLeaf } from './guards';
import { ConnectorRouting, type TConnectorRouting } from './ConnectorRouting';
import { AttachmentPoint, ConnectorEnd } from '../types';
import MindMapLeaf from './MindMapLeaf';
import Shape, { type ShapeOptions } from './Shape';
import type ShapeBase from '../shapes/ShapeBase';
import { type ShapeName } from '../shapes';
import MindMapThemes, {
  CUSTOM_THEME_FIELDS,
  DEFAULT_LEAF_HEIGHT,
  DEFAULT_LEAF_WIDTH,
  type MindMapThemeNames,
} from './MindMapThemes';
import { generateJitteredKeyBetween } from 'fractional-indexing-jittered';
import ToolResetTheme from '../tools/ToolResetTheme';
import AddMindMapLeafControl from '../controls/AddMindMapLeaf';
import type EmbeddedTextBehaviors from './EmbeddedTextBehaviorsV2';

export const MIND_MAP_LAYOUT_GAP = 128;
export const MIND_MAP_LEAF_MINIMUM_GAP = 32;

export const MINDMAP_ANIMATION_DURATION = 250;
export const MIND_MAP_DEFAULT_SIZE = 256;
export const RANK_GAP = 200;
export const NODE_GAP = 1;

const IN_ATTACHMENT_POINT = {
  east: AttachmentPoint.Left,
  west: AttachmentPoint.Right,
  north: AttachmentPoint.Bottom,
  south: AttachmentPoint.Top,
};
const OUT_ATTACHMENT_POINT = {
  east: AttachmentPoint.Right,
  west: AttachmentPoint.Left,
  north: AttachmentPoint.Top,
  south: AttachmentPoint.Bottom,
};

export type MindMapSides = 'east' | 'west' | 'south' | 'north';
export type ParentId = RecordId | `root-${MindMapSides}-${RecordId}`;
export interface NodeLayout {
  left?: number;
  top?: number;
  width?: number;
  height?: number;
}

export type MindMapOptions = Omit<ShapeOptions, 'shapeName'> &
  IObjectOptions & {
    shapeName?: ShapeName;
    sides?: MindMapSides[];
    themeName?: MindMapThemeNames;
    useTheme?: boolean;
    lineRouting?: TConnectorRouting;
  };

export interface MindMapTheme {
  label: string;
  root(mindmap?: MindMap): Partial<MindMapOptions>;
  leaf(branch: MindMapBranch, index: number): Partial<ShapeOptions>;
  connector(
    parent: MindMapLeaf | MindMap,
    child: MindMapLeaf
  ): Partial<ConnectorOptions>;
}

interface MindMap<S extends ShapeBase = ShapeBase>
  extends Shape<S>,
    LayoutBehaviors,
    FrameBehaviors {}

@FrameBehaviors
@LayoutBehaviors
class MindMap<S extends ShapeBase = ShapeBase> extends Shape<S> {
  type = 'mindMap';
  themeName: MindMapThemeNames = 'rainbow';
  /** Whether to use the theme styles, set to false when customising the style to stop it being overridden */
  useTheme = true;

  serializedKeys = [
    ...this.serializedKeys,
    'shapeName',
    'lineRouting',
    'sides',
    'themeName',
    'useTheme',
  ];

  lineRouting: TConnectorRouting;

  /** Side of the MindMap where branches sprout from */
  sides: MindMapSides[];

  deferedMindMapLayoutQueued = false;

  isMovingLeaf = false;

  /** Store previous branches when an object is removed to be read if it's added to a new parent */
  private readonly previousBranches = new WeakMap<MindMapLeaf, MindMapBranch>();

  /** Flag used to prevent infinite recursion */
  private applyingCustomTheme = false;

  constructor(options: MindMapOptions) {
    const themeName: MindMapThemeNames = options?.themeName || 'rainbow';
    const theme: MindMapTheme =
      MindMapThemes[themeName] || MindMapThemes.rainbow;
    const rootStyle = theme.root();

    super({
      width: MIND_MAP_DEFAULT_SIZE,
      height: MIND_MAP_DEFAULT_SIZE,
      textAlign: 'center',
      fontSize: 24,
      shapeName: 'circle',
      ...rootStyle,
      ...options,
    });
    this.useTheme = options.useTheme ?? true;
    // ShapeBase uses a reference to ShapeBase.defaultTextProps, we don't want to modify those, so making a copy here.
    this.shape.textProps = { ...this.shape.textProps, wrapText: 'wrapGrow' };
    this.minWidth = this.width;
    this.textMargin = 32;
    this.enableFrameBehaviors();
    this.initializeFrameMember();
    this.initializeLockBehaviors(options.locked ?? false);
    this.initializeLayout();

    this.sides = options.sides || ['east', 'west'];
    this.lineRouting =
      options.lineRouting ?? ConnectorRouting.ROUTING_ORTHOGONAL_AUTO;
    this.themeName = themeName;

    this.updateControls();

    // Track hover state for controls
    this.on('mouseover', () => {
      this.hovered = true;
      this.canvas?.requestRenderAll();
    });
    this.on('mouseout', () => {
      this.hovered = false;
      this.canvas?.requestRenderAll();
    });

    this.on('scaling', () => this.deferUpdateMindMapLayout());
    this.on('moving', () => this.layoutBranches());

    const isExistingObject = 'id' in options;

    // Delay so this.canvas exists
    setTimeout(() => {
      this.initializeMindMap(isExistingObject);

      this.canvas.on('object:moving', e => {
        if (!isMindMapLeaf(e.target) || e.target.getMindMap() !== this) return;
        this.isMovingLeaf = true;
        this.setConnectoStateForDragging(e.target, 0.2);
      });
      this.canvas.on('object:moved', e => {
        const obj = e.target;
        if (!isMindMapLeaf(obj) || obj.getMindMap() !== this) return;
        this.isMovingLeaf = false;
        obj.updateControls();

        // The last undo is completed just before this callback fires, so we'll sneak our changes into it
        const undo = this.controller.undoManager.lastUndoEntry();
        this.controller.undoManager.addUndo(undo, () => {
          // Apply theme to siblings as their colour might also change
          const branch = obj.getBranch()!;
          for (const leaf of branch.leaves) {
            this.applyLeafTheme(leaf, true);
          }

          this.updateBranchSides();
          this.recreateConnectors(obj, true);
          this.deferUpdateMindMapLayout();
        });
      });
    }, 1);
  }

  get debug() {
    return this.controller.state.debug.mindmap;
  }

  getBranches(): MindMapBranch[] {
    return this.children.filter(isMindMapBranch);
  }

  theme(): MindMapTheme {
    return MindMapThemes[this.themeName];
  }

  rootBranches(): MindMapBranch[] {
    return this.getBranches().filter(b => b.parentId?.startsWith('root-'));
  }

  containedChildren(): fabric.Object[] {
    // Vastly improve performance by ignoring connectors and leaves. They will move automatically anyway
    return this.getBranches();
  }

  private setConnectoStateForDragging(leaf: MindMapLeaf, opacity: number) {
    for (const attachment of leaf.connectorAttachments) {
      const connector = attachment.connector as MindMapConnector;
      if (attachment.whichEnd === ConnectorEnd.CONNECTOR_END) {
        // Don't hide the incoming connector, that shows where we're going
        continue;
      }

      const stroke = `color(from ${connector.stroke} srgb r g b / ${opacity})`;
      connector.stroke = stroke;
    }
    this.canvas?.requestRenderAll();
  }

  setTheme(themeName: MindMapThemeNames) {
    this.set({ themeName });
    this.applyTheme();
    this.updateMindMapLayout();
  }

  setSides(sides: MindMapSides[]) {
    const oldSides = this.sides;
    const swapMapping: Record<MindMapSides, MindMapSides> = {
      east: 'north',
      west: 'south',
      north: 'east',
      south: 'west',
    };

    // Update sides property
    this.set({ sides });

    // If changing orientation (horizontal ↔ vertical), update controls
    if (this.isChangingOrientation(oldSides, sides)) {
      this.removeAllConnectors();
      // Immediately update controls for the new orientation
      for (const branch of this.getBranches()) {
        const newSide = swapMapping[branch.side];
        const newLayoutDirection: LayoutBehaviors['layoutDirection'] =
          newSide === 'east' || newSide === 'west' ? 'vertical' : 'horizontal';

        branch.set({
          side: newSide,
          layoutDirection: newLayoutDirection,
        });
      }
      this.updateControls();
      this.canvas?.requestRenderAll();
    }

    this.updateMindMapLayout();
  }

  private isChangingOrientation(
    oldSides: MindMapSides[],
    newSides: MindMapSides[]
  ): boolean {
    const isHorizontal = (sides: MindMapSides[]) =>
      sides.includes('east') || sides.includes('west');

    const oldIsHorizontal = isHorizontal(oldSides);
    const newIsHorizontal = isHorizontal(newSides);

    // Return true if switching between horizontal and vertical orientations
    return oldIsHorizontal !== newIsHorizontal;
  }

  getConnectors(): MindMapConnector[] {
    return this.canvas
      .getObjects()
      .filter(
        obj => isMindMapConnector(obj) && obj.container === this.id
      ) as MindMapConnector[];
  }

  private removeAllConnectors(): void {
    const mindMapConnectors = this.getConnectors();

    if (mindMapConnectors.length > 0) {
      tools.ToolDelete.deleteObjects(mindMapConnectors, this.controller);
      this.canvas.requestRenderAll();
    }
  }

  setLineRouting(lineRouting: TConnectorRouting) {
    this.set({ lineRouting });
    this.applyTheme();
    this.updateMindMapLayout();
  }

  /**
   * Updates the spacing between sibling MindMapLeafs in the same branch
   */
  updateLayoutGaps() {
    // Sort branches deepest first, so we can size things in the correct order
    const branches = this.getBranches().sort((a, b) => b.depth() - a.depth());

    for (const branch of branches) {
      for (const leaf of branch.leaves) {
        leaf.updateLayoutGaps();
      }
      // Needed so branch resizes to fit the updated gaps, which determines the parent gaps
      branch.layout();
    }
  }

  applyTheme() {
    const theme = this.theme();

    if (this.useTheme) {
      this.applyRootTheme();
    }

    // Update branch styles
    for (const branch of this.getBranches()) {
      for (const leaf of branch.leaves) {
        this.applyLeafTheme(leaf);
      }
    }

    // Update root connectors
    for (const { connector } of this.connectorAttachments) {
      const [parent, child] = connector.attachments || [];
      if (parent?.object === this && isMindMapLeaf(child?.object)) {
        const connectorStyle = theme.connector(this, child.object);
        connectorStyle.lineRouting = this.lineRouting;
        connector.set(connectorStyle);
      }
    }

    this.canvas.requestRenderAll();
  }

  applyRootTheme() {
    const rootStyle = this.theme().root(this);
    ['width', 'height'].map(name => delete rootStyle[name]);
    replaceTextColor(this, rootStyle.textColor);
    this.setOptions(rootStyle);
  }

  applyLeafTheme(leaf: MindMapLeaf, recursive: boolean = false) {
    if (leaf.useTheme) {
      const branch = leaf.getBranch()!;
      const i = branch.leaves.indexOf(leaf);
      const leafStyle = this.theme().leaf(branch, i) as Partial<MindMapLeaf>;
      ['width', 'height', 'shapeName'].map(name => delete leafStyle[name]);
      replaceTextColor(leaf, leafStyle.textColor);

      // Update connector styles
      this.updateLeafConnectors(leaf);
      leaf.set(leafStyle);
      leaf.updateTextProps();
    }

    if (recursive) {
      // Update child nodes too
      const branch = this.getChildBranch(leaf.id);
      branch?.leaves.map(child => this.applyLeafTheme(child, true));
    }
  }

  updateDebug() {
    this.updateDebugRendering();
    this.updateDebugSelectability();
  }

  private updateDebugSelectability() {
    const selectable = this.debug.selectable;
    for (const obj of this.getBranches()) {
      obj.selectable = selectable;
      obj.evented = selectable;
    }
    for (const obj of this.getConnectors()) {
      obj.selectable = selectable;
      obj.evented = selectable;
    }
  }

  private updateDebugRendering() {
    for (const branch of this.getBranches()) {
      const hue = (Math.random() * 360) | 0;
      branch.fill = this.debug.showBranches
        ? `hsla(${hue}, 70%, 50%, 0.1)`
        : 'transparent';
    }
  }

  updateLeafConnectors(leaf: MindMapLeaf) {
    const theme = this.theme();
    for (const { connector } of leaf.connectorAttachments) {
      const [parent, child] = connector.attachments || [];
      if (isMindMapLeaf(parent?.object) && isMindMapLeaf(child?.object)) {
        const connectorStyle = theme.connector(parent.object, child.object);
        connectorStyle.lineRouting = this.lineRouting;
        connector.set(connectorStyle);
      }
    }
  }

  updateCustomTheme(source?: MindMapLeaf) {
    if (this.themeName === 'custom') {
      // Flag used to prevent infinite recursion when theme updates a field that triggers a theme update
      if (this.applyingCustomTheme) return;
      this.applyingCustomTheme = true;
      if (source) {
        // Partial update for efficiency
        this.applyLeafTheme(source, true);
      } else {
        // Full reapplication of theme
        this.applyTheme();
      }
      this.applyingCustomTheme = false;
    }
  }

  _set(keyOrPath: string, value: unknown): fabric.Object {
    const result = super._set(keyOrPath, value);

    // Trigger theme reapplication when fields change
    if (CUSTOM_THEME_FIELDS.includes(keyOrPath)) {
      this.updateCustomTheme();
    }

    return result;
  }

  initializeShape(changingShape = false) {
    super.initializeShape(changingShape);
    this.controls = {};
  }

  updateControls() {
    this.controls = {};
    this.persistentControls = {};

    const controls = this.persistentControls;

    const offset = 0.5;
    for (const side of this.sides) {
      switch (side) {
        case 'east':
          controls.cr = new AddMindMapLeafControl({
            x: offset,
            y: 0,
          });
          break;

        case 'west':
          controls.cl = new AddMindMapLeafControl({
            x: -offset,
            y: 0,
          });
          break;

        case 'north':
          controls.ct = new AddMindMapLeafControl({
            x: 0,
            y: -offset,
          });
          break;

        case 'south':
          controls.cb = new AddMindMapLeafControl({
            x: 0,
            y: offset,
          });
          break;
      }
    }
  }

  /**
   * Get or create the branch containing children for an object
   */
  getOrCreateBranch(
    side: MindMapSides,
    parentObject?: MindMapLeaf
  ): MindMapBranch {
    const parentId = parentObject?.id || `root-${side}-${this.id}`;

    if (!this.childBranchExists(parentId)) {
      this.createBranch(side, parentObject);
    }

    return this.getChildBranch(parentId)!;
  }

  /**
   * Get a branch by ID
   */
  getBranch(id: RecordId): MindMapBranch | undefined {
    return this.controller.dataStoreObjects.findRecordById(id) as
      | MindMapBranch
      | undefined;
  }

  /**
   * Get the branch containing an object's children
   */
  getChildBranch(parentId: ParentId): MindMapBranch | undefined {
    return this.canvas.findFirstObject(
      (o): o is MindMapBranch => isMindMapBranch(o) && o.parentId === parentId
    );
  }

  /**
   * Get the branch containing an object using its ID
   */
  getBranchContainingId(objectId: RecordId): MindMapBranch | undefined {
    const obj = this.controller.dataStoreObjects.findRecordById(objectId);
    if (!isMindMapLeaf(obj)) return;
    return obj.getBranch();
  }

  // Does a branch exist to store children for an object
  childBranchExists(parentId: ParentId): boolean {
    return !!this.getChildBranch(parentId);
  }

  /**
   * Create a branch locally, for receiving remote ops `insertBranch` is called instead.
   */
  createBranch(side: MindMapSides, parentObject?: MindMapLeaf): MindMapBranch {
    const parentId = parentObject?.id || `root-${side}-${this.id}`;
    const branch = this.createBranchFrame(side, parentId);

    if (this.debug.showBranches) {
      this.updateDebugRendering();
    }

    return branch;
  }

  /**
   * Insert a branch created from a remote op
   */
  insertBranch(branch: MindMapBranch) {
    if (this.debug.showBranches) {
      this.updateDebugRendering();
    }
    this.attachBranchEventListeners(branch);
  }

  canAddFrameMember(obj: FrameMemberBehaviors & fabric.Object): boolean {
    // Only branches can be members
    return (
      isMindMapBranch(obj) && (!obj.mindMapId || obj.mindMapId === this.id)
    );
  }

  canRemoveFrameMember(): boolean {
    // Once you join us, you'll never leave
    return false;
  }

  /**
   * Create a new child node for an object
   * @param parent {fabric.Object} Object inside the branch
   * @param index {number} Position in the list, defaults to the end
   */
  createChild(obj: MindMapLeaf, overrides?: Partial<ShapeOptions>) {
    const { side } = obj.getBranch()!;
    const branch = this.getOrCreateBranch(side, obj);
    return this.createChildInBranch(branch, overrides);
  }

  createChildInBranch(
    branch: MindMapBranch,
    overrides?: Partial<ShapeOptions>,
    index?: number
  ) {
    const style = this.theme().leaf(branch, branch.children.length);

    let textContent = overrides?.textContent ?? ' ';
    const textColor = overrides?.textColor ?? style.textColor;
    if (textContent && textColor) {
      textContent = `<p style="color:${textColor}">${textContent}</p>`;
    }
    const childObject = this.createLeaf({
      ...style,
      ...overrides,
      textContent,
    });

    branch.addMember(childObject);
    if (index != null) {
      // Insert mid layout
      const before = index > 0 ? branch.leaves[index - 1] : null;
      const after = branch.leaves[index];
      const layoutPosition = generateJitteredKeyBetween(
        before?.layoutPosition ?? null,
        after?.layoutPosition ?? null
      );
      childObject.set({ layoutPosition });
    }

    // Add empty branch for future grand children
    this.getOrCreateBranch(branch.side, childObject);
    this.reflowLayout();
    return childObject;
  }

  areConnectorsOnSameSide(
    newParentAP: AttachmentPoint,
    newChildAP: AttachmentPoint,
    existingParentAP: AttachmentPoint,
    existingChildAP: AttachmentPoint,
    side: MindMapSides
  ): boolean {
    const expectedParentAP = OUT_ATTACHMENT_POINT[side];
    const expectedChildAP = IN_ATTACHMENT_POINT[side];

    return (
      newParentAP === expectedParentAP &&
      newChildAP === expectedChildAP &&
      existingParentAP === expectedParentAP &&
      existingChildAP === expectedChildAP
    );
  }

  createConnector(
    parentObject: MindMapLeaf | MindMap,
    childObject: MindMapLeaf,
    side: MindMapSides,
    style?: Partial<ConnectorOptions>
  ): MindMapConnector {
    const themeStyle = this.theme().connector(parentObject, childObject);
    themeStyle.lineRouting = this.lineRouting;
    const connector = MindMapConnector.create(
      [
        { x: 0, y: 0 },
        { x: 0, y: 1 },
      ],
      {
        container: this.id,
        controller: this.controller,
        ...this.controller.defaultsManager.for('connector'),
        ...themeStyle,
        ...style,
        attachments: [],
        dependencyLineInfo: null,
        drawing: false,
      }
    );

    this.reattachConnector(connector, parentObject, childObject, side);
    return connector;
  }

  reattachConnector(
    connector: MindMapConnector,
    parentObject: MindMapLeaf | MindMap,
    childObject: MindMapLeaf,
    side: MindMapSides
  ) {
    switch (side) {
      case 'east':
        parentObject.attachConnector(
          connector,
          ConnectorEnd.CONNECTOR_START,
          AttachmentPoint.Right
        );
        childObject.attachConnector(
          connector,
          ConnectorEnd.CONNECTOR_END,
          AttachmentPoint.Left
        );
        break;
      case 'west':
        parentObject.attachConnector(
          connector,
          ConnectorEnd.CONNECTOR_START,
          AttachmentPoint.Left
        );
        childObject.attachConnector(
          connector,
          ConnectorEnd.CONNECTOR_END,
          AttachmentPoint.Right
        );
        break;
      case 'north':
        parentObject.attachConnector(
          connector,
          ConnectorEnd.CONNECTOR_START,
          AttachmentPoint.Top
        );
        childObject.attachConnector(
          connector,
          ConnectorEnd.CONNECTOR_END,
          AttachmentPoint.Bottom
        );
        break;
      case 'south':
        parentObject.attachConnector(
          connector,
          ConnectorEnd.CONNECTOR_START,
          AttachmentPoint.Bottom
        );
        childObject.attachConnector(
          connector,
          ConnectorEnd.CONNECTOR_END,
          AttachmentPoint.Top
        );
        break;
    }
    parentObject.positionConnectors();

    return connector;
  }

  removeConnectorTo(childObject: MindMapLeaf) {
    const parentBranch = childObject.getBranch()!;
    const ap = IN_ATTACHMENT_POINT[parentBranch.side];
    for (const attachment of childObject.connectorAttachments) {
      const { connector, attachmentPoint } = attachment;
      if (attachmentPoint === ap && isMindMapConnector(connector)) {
        tools.ToolDelete.deleteObjects([connector], this.controller);
      }
    }
  }

  recreateConnectors(obj: MindMapLeaf, recursive: boolean = false) {
    const branch = obj.getBranch();
    if (!branch) return;

    const connectorsToRemove = obj.connectorAttachments
      .map(att => att.connector)
      .filter(isMindMapConnector);
    if (connectorsToRemove.length > 0) {
      tools.ToolDelete.deleteObjects(connectorsToRemove, this.controller);
    }

    if (branch.parentId?.startsWith('root-')) {
      this.createConnector(this, obj, branch.side);
    } else {
      const parent = this.controller.dataStoreObjects.findRecordById(
        branch.parentId as RecordId
      );
      if (isMindMapLeaf(parent)) {
        this.createConnector(parent, obj, branch.side);
      }
    }

    const childBranch = this.getChildBranch(obj.id);
    if (childBranch) {
      for (const child of childBranch.leaves) {
        this.createConnector(obj, child, childBranch.side);
        if (recursive) {
          this.recreateConnectors(child, true);
        }
      }
    }
  }

  deferUpdateMindMapLayout() {
    if (this.deferedMindMapLayoutQueued) return;

    this.deferedMindMapLayoutQueued = true;
    queueMicrotask(() => {
      const undo = this.controller.undoManager.lastUndoEntry();
      this.controller.undoManager.addUndo(undo, () => {
        this.updateMindMapLayout();
      });
      this.deferedMindMapLayoutQueued = false;
    });
  }

  updateMindMapLayout() {
    if (this.isMovingLeaf) {
      // Don't update layout while we're dragging things around
      return;
    }
    this.reflowLayout();
    this.createMissingConnectors();
  }

  reflowLayout() {
    this.layoutBranches();
    this.updateLayoutGaps();
    this.syncBranchesWithLayout(this.animationDuration() > 0);
  }

  // Force layout of children on all branches
  layoutBranches() {
    for (const branch of this.getBranches()) {
      branch.layout();
    }
  }

  /**
   * Update the position of the MindMapBranch objects to match a simple layout
   */
  syncBranchesWithLayout(animate?: boolean) {
    // Sort branches top first, so we can position things in the correct order
    const branches = this.getBranches().sort((a, b) => a.depth() - b.depth());

    const gap = MIND_MAP_LAYOUT_GAP;
    for (const branch of branches) {
      const size = {
        x: branch.getScaledWidth(),
        y: branch.getScaledHeight(),
      };

      const point = { left: 0, top: 0 };
      const parent = branch.getParentLeaf() || branch.getMindMap()!;
      const parentPosition = { left: parent.left, top: parent.top };
      if (isMindMapLeaf(parent)) {
        const parentBranch = parent.getBranch();
        if (parentBranch) {
          // During animation we could be anywhere, so we compensate by add the parent branch's difference between current position and final destination
          if (parentBranch.animationTargets.left != null) {
            parentPosition.left -=
              parentBranch.left - parentBranch.animationTargets.left;
          }
          if (parentBranch.animationTargets.top != null) {
            parentPosition.top -=
              parentBranch.top - parentBranch.animationTargets.top;
          }
        }
      }

      switch (branch.side) {
        case 'east':
        case 'west':
          point.top =
            parentPosition.top + parent.getScaledHeight() / 2 - size.y / 2;
          break;
        case 'north':
        case 'south':
          point.left =
            parentPosition.left + parent.getScaledWidth() / 2 - size.x / 2;
          break;
      }

      switch (branch.side) {
        case 'west':
          point.left = parentPosition.left - size.x - gap;
          break;
        case 'east':
          point.left = parentPosition.left + parent.getScaledWidth() + gap;
          break;

        case 'north':
          point.top = parentPosition.top - size.y - gap;
          break;
        case 'south':
          point.top = parentPosition.top + parent.getScaledHeight() + gap;
          break;
      }

      if (animate && branch.children.length > 0) {
        const easing = fabric.util.ease.easeOutCubic;
        const onComplete = () => {};
        branch.animate(point, {
          easing,
          duration: this.animationDuration(),
          onComplete,
        });
      } else {
        branch.set(point);
      }
    }
  }

  initializeMindMap(restore: boolean) {
    if (restore) {
      // Existing instance, just add event listeners
      for (const branch of this.getBranches()) {
        this.attachBranchEventListeners(branch);
      }
    } else {
      // New instance, create root nodes
      for (const side of this.sides) {
        const branch = this.getOrCreateBranch(side);
        const style = this.theme().leaf(branch, 0);
        const firstLeaf = this.createLeaf(style);
        branch.addMember(firstLeaf);
        this.getOrCreateBranch(side, firstLeaf);
      }

      this.canvas.setActiveObject(this);
      this.startTextEditing(true);
    }

    this.updateMindMapLayout();
  }

  createBranchFrame(side: MindMapSides, parentId?: ParentId): MindMapBranch {
    const layoutDirection: LayoutBehaviors['layoutDirection'] =
      side === 'east' || side === 'west' ? 'vertical' : 'horizontal';
    const left = this.left;
    const top = this.top;
    const width = DEFAULT_LEAF_WIDTH;
    const height = DEFAULT_LEAF_HEIGHT;

    let layoutMinWidth: number | undefined;
    let layoutMinHeight: number | undefined;

    if (parentId && !parentId.startsWith('root-')) {
      const parentObject = this.canvas.findFirstObject(
        (o): o is MindMapLeaf => o.id === parentId
      );
      if (parentObject) {
        // Include stroke width in the dimensions calculation
        layoutMinWidth = parentObject.width * parentObject.scaleX;
        layoutMinHeight = parentObject.height * parentObject.scaleY;

        if (parentObject.strokeWidth) {
          layoutMinWidth += parentObject.strokeWidth;
          layoutMinHeight += parentObject.strokeWidth;
        }
      }
    }

    const branch = new MindMapBranch({
      // ID of the object/shape that spawned this branch
      parentId,
      mindMapId: this.id,
      side,
      controller: this.controller,
      layoutDirection,
      left,
      top,
      width,
      height,
      layoutMinWidth,
      layoutMinHeight,
    });

    this.controller.addToCanvas(branch);
    this.addMember(branch);
    branch.container = this.id;
    this.attachBranchEventListeners(branch);

    return branch;
  }

  attachBranchEventListeners(branch: MindMapBranch) {
    branch.on('member:added', e => this.onNodeAdded(e));
    branch.on('member:removed', e => this.onNodeRemoved(e));
    branch.on('member:moved', e => this.onNodeMoved(e));
  }

  onNodeAdded(e: IEvent<Event>): void {
    const obj = e.target;
    // Branch we were added too
    const branch = e.currentTarget;
    if (!isMindMapLeaf(obj) || !isMindMapBranch(branch)) {
      return;
    }
    const previousBranch = this.previousBranches.get(obj);
    if (previousBranch?.side !== obj.getBranch()?.side) {
      this.recreateConnectors(obj, true);
    }

    if (branch.parentId?.startsWith('root-')) {
      // Root node connect to root shape
      this.createConnector(this, obj, branch.side);
    } else {
      const parent = this.controller.dataStoreObjects.findRecordById(
        branch.parentId as RecordId
      );
      if (isMindMapLeaf(parent)) {
        this.createConnector(parent, obj, branch.side);
      }
    }

    this.updateMindMapLayout();
  }

  onNodeMoved(e: IEvent<Event>): void {
    const obj = e.target;
    const branch = e.currentTarget;
    if (!isMindMapLeaf(obj) || !isMindMapBranch(branch)) {
      return;
    }
    if (this.canvas.getActiveObject() === obj) {
      // Deselect object because the toolbar is annoying when dragging things around
      this.canvas.discardActiveObject();
    }
    this.updateMindMapLayout();
  }

  onNodeRemoved(e: IEvent<Event>): void {
    const obj = e.target;
    // Branch we were removed from
    const branch = e.currentTarget;
    if (!isMindMapLeaf(obj) || !isMindMapBranch(branch)) {
      return;
    }
    this.previousBranches.set(obj, branch);
    this.removeConnectorTo(obj);

    this.updateMindMapLayout();
  }

  /**
   * Create a new shape object ready to put inside a MindMapBranch
   */
  createLeaf(style: Partial<ShapeOptions>): MindMapLeaf {
    return MindMapLeaf.create({
      controller: this.canvas.controller,
      top: this.top,
      left: this.top + this.width + 200,
      angle: 0,
      width: 150,
      height: 48,
      shapeName: 'circle',
      textContent: ' ',
      ...style,
    });
  }

  updateBranchSides() {
    const traverseAndFix = (
      branch: MindMapBranch,
      expectedSide: MindMapSides
    ) => {
      if (branch.side !== expectedSide) {
        branch.set('side', expectedSide);
      }

      for (const leaf of branch.leaves) {
        const childBranch = this.getChildBranch(leaf.id);
        if (childBranch) {
          traverseAndFix(childBranch, expectedSide);
        }
      }
    };

    for (const rootBranch of this.rootBranches()) {
      traverseAndFix(rootBranch, rootBranch.side);
    }
  }

  tools(topLevel = true) {
    const { isArray } = Array;
    const baseTools = [...super.tools(topLevel)];

    // Add mindmap group near the start
    const mindMapTools = [
      tools.ToolMindMapTheme,
      tools.ToolMindMapOrientation,
      tools.ToolMindMapConnectorStyle,
    ];
    baseTools.splice(1, 0, mindMapTools);

    // Add 'reset theme' button to color/style section
    if (isArray(baseTools[3])) {
      baseTools[3].unshift(ToolResetTheme);
    } else {
      baseTools.unshift(ToolResetTheme);
    }
    return baseTools;
  }

  endTextEditing() {
    super.endTextEditing();
    if (!this.textEditing) return;
    this.updateMindMapLayout();
  }

  animationDuration() {
    return MINDMAP_ANIMATION_DURATION * (this.debug.slowAnimations ? 10 : 1);
  }

  /**
   * Checks all MindMapLeaves in the MindMap and creates any missing MindMapConnectors
   */
  createMissingConnectors() {
    const allLeaves = this.getAllLeaves();

    for (const leaf of allLeaves) {
      const branch = leaf.getBranch();
      if (!branch) continue;

      // Check if this leaf needs a connector from its parent
      const needsConnector = this.leafNeedsConnector(leaf, branch);

      if (needsConnector) {
        if (branch.parentId?.startsWith('root-')) {
          // Root node - connect to the MindMap center
          this.createConnector(this, leaf, branch.side);
        } else {
          // Non-root node - connect to parent leaf
          const parent = this.controller.dataStoreObjects.findRecordById(
            branch.parentId as RecordId
          );
          if (isMindMapLeaf(parent)) {
            this.createConnector(parent, leaf, branch.side);
          }
        }
      }
    }
  }

  /**
   * Gets all MindMapLeaves in this MindMap
   */
  getAllLeaves(): MindMapLeaf[] {
    const leaves: MindMapLeaf[] = [];

    for (const branch of this.getBranches()) {
      leaves.push(...branch.leaves);
    }

    return leaves;
  }

  /**
   * Checks if a leaf needs a connector based on expected attachment points for its side
   */
  leafNeedsConnector(leaf: MindMapLeaf, branch: MindMapBranch): boolean {
    const expectedAttachmentPoint = IN_ATTACHMENT_POINT[branch.side];

    // Check if the leaf has a connector attached at the expected attachment point
    const hasExpectedConnector = leaf.connectorAttachments.some(
      attachment => attachment.attachmentPoint === expectedAttachmentPoint
    );

    return !hasExpectedConnector;
  }
}

function replaceTextColor(
  obj: fabric.Object & EmbeddedTextBehaviors,
  newColor?: string
) {
  if (obj.textContent) {
    const textContent = obj.textContent
      .replaceAll(/ style="color:[^"]*"/g, '')
      .replaceAll('<p>', `<p style="color:${newColor}">`);
    obj.set({ textContent });
  }
}

export default MindMap;
